// views/DeliveryFee/CompetitivenessCue.jsx
// ─────────────────────────────────────────────────────────────────────────────
// A small, honest "competitiveness" cue. Shows the effective fee-per-km for this
// quote and places it against the zone's typical per-km band, derived from the
// live config (config.pricing.zones[zone].tiers rates). Helps a vendor see that
// the price is in line with the zone — without gimmicky "cheapest!" claims.
//
// The "typical band" is just the min→max of the zone's configured per-km tier
// rates. We add the base fare spread over the distance so the comparison is
// apples-to-apples with the fee-per-km we display.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { FiActivity, FiInfo } from "react-icons/fi";
import { C, zoneMeta } from "./feeUiKit.js";

// Pull the per-km rate range for a zone out of the pricing config.
function zoneRateBand(config, zone) {
  const zp = config?.pricing?.zones?.[zone] || config?.pricing?.zones?.urban;
  if (!zp || !Array.isArray(zp.tiers) || zp.tiers.length === 0) return null;
  const rates = zp.tiers.map((t) => Number(t.rate)).filter((r) => Number.isFinite(r) && r > 0);
  if (rates.length === 0) return null;
  return {
    min: Math.min(...rates),
    max: Math.max(...rates),
    baseFare: Number(zp.baseFare || 0),
  };
}

export default function CompetitivenessCue({ quote, config }) {
  if (!quote || !config) return null;

  const zm = zoneMeta(quote.zone);
  const km = Number(quote.distanceKm || 0);
  const fee = Number(quote.fee || 0);
  if (km <= 0 || fee <= 0) return null;

  const perKm = fee / km;
  const band = zoneRateBand(config, quote.zone);

  // Typical effective per-km = tier rate + (base fare amortised over this trip).
  // Gives a fair "what a plain tier-only fee would look like per km" envelope.
  const baseShare = band ? band.baseFare / km : 0;
  const bandLo = band ? band.min + baseShare : null;
  const bandHi = band ? band.max + baseShare : null;

  // Position the marker within [lo, hi] (clamped) for the little gauge.
  let pos = 50;
  let verdict = { label: "In line with the zone", color: C.teal };
  if (bandLo != null && bandHi != null && bandHi > bandLo) {
    pos = ((perKm - bandLo) / (bandHi - bandLo)) * 100;
    if (perKm < bandLo) {
      pos = 0;
      verdict = { label: "Below typical — keenly priced", color: C.teal };
    } else if (perKm > bandHi) {
      pos = 100;
      verdict = { label: "Above typical for this zone", color: C.orange };
    } else if (pos <= 40) {
      verdict = { label: "Toward the lower end", color: C.teal };
    } else if (pos >= 70) {
      verdict = { label: "Toward the higher end", color: C.orange };
    }
  }
  pos = Math.max(0, Math.min(100, pos));

  const fmtRate = (r) => (r == null ? "—" : `UGX ${Math.round(r).toLocaleString()}/km`);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.text, lineHeight: 1 }}>
            {fmtRate(perKm)}
          </div>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>
            Effective fee per km · {zm.label} zone
          </div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700, color: verdict.color,
          background: `${verdict.color}14`, border: `1px solid ${verdict.color}33`,
          borderRadius: 20, padding: "4px 12px", whiteSpace: "nowrap",
        }}>
          {verdict.label}
        </span>
      </div>

      {band ? (
        <>
          {/* Gauge: typical band with the current quote marked */}
          <div style={{ position: "relative", height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${C.tealSoft}, ${C.orangeSoft})`, marginTop: 14, marginBottom: 4 }}>
            <div style={{
              position: "absolute", top: -3, left: `calc(${pos}% - 7px)`,
              width: 14, height: 14, borderRadius: "50%", background: verdict.color,
              border: "2px solid #fff", boxShadow: C.shadow,
            }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.textLight }}>
            <span>Typical low · {fmtRate(bandLo)}</span>
            <span>Typical high · {fmtRate(bandHi)}</span>
          </div>

          <div style={{ fontSize: 11, color: C.textLight, marginTop: 10, display: "flex", gap: 6, alignItems: "flex-start" }}>
            <FiInfo size={11} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>
              The band is this zone's configured per-km tiers ({fmtRate(band.min + baseShare)}–{fmtRate(band.max + baseShare)} at {km.toFixed(1)} km, base fare included). Longer trips fall to lower per-km rates by design.
            </span>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 10, display: "flex", gap: 6, alignItems: "center" }}>
          <FiActivity size={11} /> No tier band configured for this zone to compare against.
        </div>
      )}
    </div>
  );
}
